import FieldLabel from '@components/Common/FieldLabel';
import type { TextFieldProps } from '@mui/material';
import { Grid, TextField, Typography } from '@mui/material';
import React from 'react';
import './index.scss';

interface FormFieldProps {
  // Basic form props
  name: string;
  label: string;
  value?: any;
  onChange: (name: string, value: any) => void;
  
  // Input type variations. Forwarded straight to MUI's TextField.
  type?: 'text' | 'number' | 'email' | 'password' | 'tel' | 'url' | 'date' | 'month';
  multiline?: boolean;
  rows?: number;
  
  // Validation & state
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  
  // Auto-validation support (if formData and errors are provided)
  formData?: Record<string, any>;
  errors?: Record<string, string>;
  
  // Grid integration
  gridProps?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  
  // Custom styling
  className?: string;
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  
  
  // Additional TextField props passthrough
  textFieldProps?: Partial<TextFieldProps>;
}

const FormField: React.FC<FormFieldProps> = ({
  name,
  label,
  value,
  onChange,
  type = 'text',
  multiline = false,
  rows,
  required = false,
  disabled = false,
  error,
  helperText,
  placeholder,
  formData,
  errors,
  gridProps,
  className = 'form-field',
  variant = 'outlined',
  size = 'medium',
  fullWidth = true,
  textFieldProps = {},
}) => {
  // Auto-validation: if formData and errors are provided, use them
  const autoError = formData && errors ? !!errors[name] : error;
  const autoHelperText = formData && errors ? errors[name] : helperText;
  const autoValue = formData ? (formData[name] ?? '') : (value ?? '');
  const hasValue = autoValue !== '' && autoValue !== null && autoValue !== undefined;

  // Handle value changes with type conversion
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    let convertedValue: any = newValue;

    // Convert value based on type
    if (type === 'number') {
      convertedValue = newValue === '' ? '' : Number(newValue);
    }

    onChange(name, convertedValue);
  };

  /*
   * Fill, border, radius, padding and font size all come from theme.ts MuiOutlinedInput, including
   * the multiline root reset that used to live here. A local fix meant every multiline field that
   * was not a FormField kept MUI's padding stacked on ours.
   */
  const textField = (
    <div className="form-field-container">
      {/* Skip the label element entirely when unlabelled, otherwise an empty <label> still
          occupies its margin. Mirrors AutocompleteFormField, which already guards this.

          htmlFor ties it to the input below. Without it the label was decoration: clicking it did
          not focus the field, and a screen reader read the input unlabelled. The field name is the
          id, which is unique per form and is what a test would guess. */}
      {label && (
        <FieldLabel required={required} disabled={disabled} htmlFor={name}>{label}</FieldLabel>
      )}
      {disabled ? (
        <Typography
          className={`disabled-view-typography ${multiline ? 'multiline' : ''}`}
          variant="body1"
        >
          {hasValue ? String(autoValue) : (placeholder || '-')}
        </Typography>
      ) : (
        <TextField
          className={className}
          disabled={disabled}
          error={autoError}
          id={name}
          fullWidth={fullWidth}
          helperText={autoHelperText}
          multiline={multiline}
          name={name}
          placeholder={placeholder}
          required={required}
          rows={multiline ? rows || 1 : undefined}
          size={size}
          type={type}
          value={autoValue}
          variant={variant}
          onChange={handleChange}
          {...textFieldProps}
        />
      )}
    </div>
  );

  // If gridProps are provided, wrap in Grid item
  if (gridProps) {
    return (
      <Grid {...gridProps}>
        {textField}
      </Grid>
    );
  }

  return textField;
};

export default FormField;
